import type { GuestCommunicationMessageKey } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { OccasionButton, OccasionNode } from './JourneyOccasion';
import { JourneyStatus } from './JourneyStatus';
import { OccasionControl } from './JourneyControl';
import { LAST_REMINDER_LABEL, isLastReminder, switchLabel, type Occasion } from './journeyModel';
import type { SwitchChange, SwitchOutcome } from './useCatalogSwitch';

// Breit liegt jeder Anlass in seiner Spalte: Zeitpunkt, Punkt, Auswahl, Schalter untereinander.
const COLUMN = [
  '@2xl:col-start-1',
  '@2xl:col-start-2',
  '@2xl:col-start-3',
  '@2xl:col-start-4',
  '@2xl:col-start-5',
];

interface Props {
  occasions: Occasion[];
  selected: GuestCommunicationMessageKey;
  onSelect: (key: GuestCommunicationMessageKey) => void;
  editable: boolean;
  /** Während Texte gespeichert werden, ruhen die Schalter — beide gehen gegen dieselbe Revision. */
  busy: boolean;
  pending?: SwitchChange;
  outcome: SwitchOutcome | null;
  onSwitch: (change: SwitchChange) => void;
  onUndo: () => void;
}

/**
 * Die Gastreise: fünf Anlässe in fester Zeitfolge. Ein Aufbau für beide Breiten — breit ein
 * Raster mit Zeitleiste, schmal eine Liste —, damit jeder Schalter nur einmal existiert.
 */
export function CommunicationJourney(props: Props) {
  const { occasions } = props;
  const shown = (occasion: Occasion) => {
    const { pending } = props;
    const affected =
      pending &&
      (pending.key === occasion.key ||
        (isLastReminder(pending.key) && isLastReminder(occasion.key)));
    return affected ? pending.enabled : occasion.enabled;
  };
  const column = (occasion: Occasion) => COLUMN[occasions.indexOf(occasion)];
  const control = (occasion: Occasion, label: string) => (
    <OccasionControl
      label={label}
      occasion={occasion}
      enabled={shown(occasion)}
      editable={props.editable}
      busy={props.busy}
      pending={!!props.pending}
      onChange={(enabled) => props.onSwitch({ key: occasion.key, enabled })}
    />
  );
  const part = (occasion: Occasion) => (
    <>
      <span
        aria-hidden
        className={cn(
          'text-ink-soft @2xl:row-start-1 @2xl:block hidden h-[22px] whitespace-nowrap px-0.5 font-mono text-[10.5px] uppercase tracking-[0.06em]',
          column(occasion),
        )}
      >
        {occasion.when}
      </span>
      <OccasionNode
        required={occasion.required}
        enabled={shown(occasion)}
        className={cn('@2xl:row-start-2 @2xl:mb-2.5 @2xl:ml-0.5', column(occasion))}
      />
      <div className={cn('@2xl:row-start-3 min-w-0 self-stretch', column(occasion))}>
        <OccasionButton
          occasion={occasion}
          enabled={shown(occasion)}
          selected={props.selected === occasion.key}
          onSelect={() => props.onSelect(occasion.key)}
        />
      </div>
    </>
  );
  const pair = occasions.filter((occasion) => isLastReminder(occasion.key));

  return (
    <section
      aria-label="Gastreise"
      className="border-line/50 bg-card shadow-card @container rounded-lg border p-4"
    >
      <div className="@2xl:grid @2xl:grid-cols-5 @2xl:gap-x-2.5 flex flex-col gap-2.5">
        <span
          aria-hidden
          className="bg-line @2xl:col-span-5 @2xl:col-start-1 @2xl:row-start-2 @2xl:mx-2 @2xl:mt-[6px] @2xl:block hidden h-0.5 self-start rounded-full"
        />
        {occasions.map((occasion) =>
          isLastReminder(occasion.key) ? (
            occasion === pair[0] && (
              <div
                key="last-reminder"
                className="border-line @2xl:contents rounded-xl border-[1.5px] border-dashed p-2.5"
              >
                <div className="text-ink-soft @2xl:col-span-2 @2xl:col-start-3 @2xl:row-start-4 @2xl:mt-2 @2xl:rounded-b-xl @2xl:border-[1.5px] @2xl:border-t-0 @2xl:border-dashed @2xl:border-line @2xl:px-3 flex items-center justify-between gap-2 text-xs">
                  <span>
                    <span className="text-ink font-semibold">{LAST_REMINDER_LABEL}</span> · Vortag
                    oder Anreisetag, nie beide
                  </span>
                  {control(occasion, `${LAST_REMINDER_LABEL} aktiv`)}
                </div>
                {pair.map((member) => (
                  <div
                    key={member.key}
                    className="@2xl:contents mt-2 grid grid-cols-[0.875rem_minmax(0,1fr)] items-center gap-x-3"
                  >
                    {part(member)}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div
              key={occasion.key}
              className="@2xl:contents grid grid-cols-[0.875rem_minmax(0,1fr)_6.75rem] items-center gap-x-3"
            >
              {part(occasion)}
              <div className={cn('@2xl:row-start-4 @2xl:pt-1', column(occasion))}>
                {control(occasion, `${occasion.title} aktiv`)}
              </div>
            </div>
          ),
        )}
      </div>
      <JourneyStatus
        outcome={props.outcome}
        labelFor={(key) => switchLabel(occasions, key)}
        busy={props.busy || !!props.pending}
        onUndo={props.onUndo}
      />
    </section>
  );
}
